import { useState } from "react";
import axios from "axios";
import LegalLayout from "@/components/LegalLayout";
import { useLanguage, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ContactUs() {
  const { language, t } = useLanguage();
  const ar = language === "ar";
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(ar ? "املأ الحقول المطلوبة" : "Fill required fields");
      return;
    }
    setSending(true);
    try {
      // POST to backend → stored in MongoDB + forwarded to owner inbox via Resend +
      // auto-ack email is sent back to the user.
      await axios.post(`${API}/contact`, form);
      toast.success(ar
        ? "تم إرسال رسالتك بنجاح! سنرد عليك خلال 1-2 يوم عمل."
        : "Your message was sent! We will reply within 1–2 business days.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || (ar ? "تعذّر إرسال الرسالة. حاول مرة أخرى." : "Could not send. Please try again."));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white focus:border-yellow-500 outline-none w-full";

  return (
    <LegalLayout titleAr="اتصل بنا" titleEn="Contact Us">
      <p className="text-lg">{ar
        ? "نحب أن نسمع منك. أرسل لنا رسالة وسنرد خلال 1-2 يوم عمل."
        : "We'd love to hear from you. Send us a message and we'll reply within 1–2 business days."}</p>

      <div className="grid md:grid-cols-3 gap-4 not-prose my-6">
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-start gap-3">
          <Mail className="text-yellow-500 flex-shrink-0" size={20} />
          <div>
            <p className="text-xs text-slate-400">{ar ? "البريد العام" : "General"}</p>
            <a href="mailto:support@arabsanad.com" className="text-white text-sm font-semibold hover:text-yellow-400">support@arabsanad.com</a>
          </div>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-start gap-3">
          <Mail className="text-yellow-500 flex-shrink-0" size={20} />
          <div>
            <p className="text-xs text-slate-400">{ar ? "الفوترة" : "Billing"}</p>
            <a href="mailto:billing@arabsanad.com" className="text-white text-sm font-semibold hover:text-yellow-400">billing@arabsanad.com</a>
          </div>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-start gap-3">
          <MapPin className="text-yellow-500 flex-shrink-0" size={20} />
          <div>
            <p className="text-xs text-slate-400">{ar ? "العنوان" : "Address"}</p>
            <p className="text-white text-sm font-semibold">Delaware, USA</p>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="not-prose space-y-4 bg-slate-900/60 border border-slate-800 rounded-2xl p-5" data-testid="contact-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-slate-400 block mb-1">{ar ? "الاسم *" : "Name *"}</label>
            <Input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
          </div>
          <div>
            <label className="text-sm text-slate-400 block mb-1">{ar ? "البريد *" : "Email *"}</label>
            <Input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
          </div>
        </div>
        <div>
          <label className="text-sm text-slate-400 block mb-1">{ar ? "الموضوع" : "Subject"}</label>
          <Input className={inputCls} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
        </div>
        <div>
          <label className="text-sm text-slate-400 block mb-1">{ar ? "الرسالة *" : "Message *"}</label>
          <textarea rows={5} className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
        </div>
        <Button type="submit" disabled={sending} className="btn-primary" data-testid="contact-send-btn">
          {sending ? <Loader2 className="animate-spin" size={18} /> : <><Send size={18} className="ml-2" />{ar ? "إرسال" : "Send"}</>}
        </Button>
      </form>
    </LegalLayout>
  );
}
